import {
  MODULOS_VENDIBLES,
  PRECIOS_POR_MODULO,
  centimosDelIntervalo,
  deLaClaveDeBusqueda,
  type AreaDeModulo,
} from "./precios"
import type { LineaDeSuscripcion } from "./suscripcion-de-modulos"

/**
 * El pack por area: con tres modulos de un area un 20%, con todos un 30%.
 *
 * El modelo ya vivia en `factura-de-modulos.ts`, que calcula lo que cuesta una
 * empresa. Se mueve aqui porque ahora lo necesitan tambien la pantalla y lo que
 * se le manda a Stripe, y tres copias de una regla de precio acaban diciendo
 * cosas distintas. `factura-de-modulos.ts` lo importa de aqui.
 *
 * SE DEFINE POR PROPORCION Y NO POR LISTA, a proposito: un pack enumerado se
 * rompe en silencio el dia que entra un modulo nuevo en el area.
 */

/**
 * Cuantos modulos vendibles tiene cada area.
 *
 * Sale del catalogo y no de un objeto escrito a mano: un area nueva empezaria en
 * `undefined` y el `+= 1` la dejaria en NaN, o sea sin umbral de pack que valga.
 */
export const CUANTOS_POR_AREA: Record<AreaDeModulo, number> = MODULOS_VENDIBLES.reduce(
  (cuenta, modulo) => {
    const area = PRECIOS_POR_MODULO[modulo].area
    cuenta[area] = (cuenta[area] ?? 0) + 1
    return cuenta
  },
  {} as Record<AreaDeModulo, number>,
)

export function porcentajeDelPack(encendidos: number, total: number): number {
  if (encendidos >= total && total > 0) return 30
  if (encendidos >= 3) return 20
  return 0
}

/** Que porcentaje le toca a cada area. Solo aparecen las que cualifican. */
export function packsPorArea(modulos: readonly string[]): Partial<Record<AreaDeModulo, number>> {
  const cuenta = {} as Record<AreaDeModulo, number>
  for (const modulo of new Set(modulos)) {
    // Una clave que no se vende --`cierre_caja`, `personal`-- no cuenta para el
    // pack: no tiene precio ni area, y colarla regalaria un descuento.
    if (!(MODULOS_VENDIBLES as readonly string[]).includes(modulo)) continue
    const area = PRECIOS_POR_MODULO[modulo as (typeof MODULOS_VENDIBLES)[number]].area
    cuenta[area] = (cuenta[area] ?? 0) + 1
  }

  const packs: Partial<Record<AreaDeModulo, number>> = {}
  for (const area of Object.keys(cuenta) as AreaDeModulo[]) {
    const porcentaje = porcentajeDelPack(cuenta[area], CUANTOS_POR_AREA[area])
    if (porcentaje > 0) packs[area] = porcentaje
  }
  return packs
}

/**
 * Lo que se le descuenta a CADA LINEA, que es como lo va a aplicar Stripe.
 *
 * Por linea y no sobre la suma del area a proposito: Stripe aplica un cupon de
 * porcentaje a cada linea de suscripcion, asi que calcularlo de otra forma
 * meteria diferencias de redondeo entre lo que enseña la pantalla y lo que sale
 * en la factura. Con los precios de hoy no hay redondeo ninguno --todos son
 * multiplos de diez centimos-- y `packs-de-area.test.ts` vigila que siga asi.
 *
 * El nucleo no lleva pack: no es de ningun area.
 */
export function descuentosDeLasLineas(params: {
  lineas: readonly LineaDeSuscripcion[]
  packs: Partial<Record<AreaDeModulo, number>>
}): { claveDeBusqueda: string; porcentaje: number; centimos: number }[] {
  const descuentos: { claveDeBusqueda: string; porcentaje: number; centimos: number }[] = []

  for (const linea of params.lineas) {
    const leida = deLaClaveDeBusqueda(linea.claveDeBusqueda)
    if (!leida || leida.concepto === "nucleo") continue

    const precio = PRECIOS_POR_MODULO[leida.concepto]
    const porcentaje = params.packs[precio.area] ?? 0
    if (porcentaje === 0) continue

    const totalDeLaLinea = centimosDelIntervalo(precio.centimosMensuales, leida.intervalo) * linea.cantidad
    // Hacia abajo: el redondeo, si algun dia lo hubiera, siempre a favor de quien
    // paga.
    const centimos = Math.floor((totalDeLaLinea * porcentaje) / 100)
    if (centimos > 0) descuentos.push({ claveDeBusqueda: linea.claveDeBusqueda, porcentaje, centimos })
  }

  return descuentos
}
